import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { usePermission } from '@/lib/contexts/permission-context';

interface ProtectedRouteProps {
    resource: string;
    action?: string;
    children: ReactNode;
}

/**
 * Route guard component that protects entire routes based on permissions
 * 
 * @example
 * <ProtectedRoute resource="members" action="read">
 *   <MembersPage />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
    resource,
    action = 'read',
    children,
}: ProtectedRouteProps) {
    const { hasPermission, loading } = usePermission();

    // Show loading state while checking permissions
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Checking permissions...</p>
                </div>
            </div>
        );
    }

    // Check if user has the required permission
    if (!hasPermission(resource, action)) {
        // Redirect to 403 Forbidden page with context
        return (
            <Navigate
                to="/403"
                state={{
                    requiredPermission: `${resource}.${action}`,
                    attemptedPath: window.location.pathname,
                }}
                replace
            />
        );
    }

    // User has permission, render the protected content
    return <>{children}</>;
}
